/**
 * Facts registry — resolves PagePlan `FactBinding`s against the underlying
 * source JSON, formatted per the binding's `format` field and the page
 * language.
 *
 * Reuses the existing per-source react-query hooks so cache + freshness
 * stay consistent across the app.  When any source is still loading, the
 * registry's `ready` flag is false; the page renderer can fall back to
 * skeleton state.
 *
 * Adding a new source: register a loader entry in SOURCE_LOADERS below
 * AND update `FactSource` in page-plan.ts.
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { countriesQuery, type Country } from '@/data';
import { usePropertyTax } from '@/features/property-tax';
import { useCarOwnership } from '@/features/car-ownership';
import { useCountryTaxRegimes } from '@/features/quiz/tax-regimes';
import { useCountryVisaExtra } from '@/features/quiz/visa-extra';
import { usePassportTreatment } from '@/features/quiz/passport-treatment';
import { useCostOfLiving } from '@/features/quiz/cost-of-living';
import type { CountryCode, LangCode } from './architecture';
import type { FactBinding, FactFormat, FactSource } from './page-plan';

/* ────────────────────────────────────────────────────────────────── */
/*  Formatters                                                         */
/* ────────────────────────────────────────────────────────────────── */

const RU_NUM = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 });
const EN_NUM = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });

/* Money keeps its cents.
 *
 * `num()` rounds to whole units, which is right for a rent or a salary and
 * destroys a fare. Athens sells a 90-minute ticket for 1.20 and Thessaloniki
 * one for 0.60; through `maximumFractionDigits: 0` those render as "€ 1" and
 * "€ 1", the same price for two different tickets, and the Attiki Odos toll of
 * 2.55 renders as "€ 3". Ninety-four bound currency facts across six live
 * countries carry a fractional value, including Estonia's 3.50 prescription
 * deductible ("€ 4") and Spain's 0.20 per kWh, which rounded to "€ 0".
 *
 * A fact author writes 1.20 rather than 1.2 because the precision is the
 * point, so an amount that is not a whole number is shown to two decimals and
 * one that is stays clean. */
const RU_MONEY = new Intl.NumberFormat('ru-RU', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const EN_MONEY = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const RU_PCT = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 2 });
const EN_PCT = new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 });
const PCT_NUM = (lang: LangCode) => (lang === 'ru' ? RU_PCT : EN_PCT);

/* Sub-unit tariffs keep their published precision.
 *
 * Two decimals is right for a price, and wrong for a per-unit rate that a
 * regulator publishes to four: Thailand's Ft charge of 0.1623 baht per unit
 * printed as "0.16", its tier rates 4.1584 and 4.3583 as "4.16" and "4.36",
 * and the Bank of Thailand's 38.3566 baht per euro as "38.36". Only small
 * values with more than two decimals widen, so a salary or a rent computed to
 * three places still prints as money. */
const RU_RATE = new Intl.NumberFormat('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 4 });
const EN_RATE = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 });

function money(value: number, lang: LangCode): string {
  if (Number.isInteger(value)) return num(value, lang);
  const hasSubCentPrecision = Math.abs(value) < 100 && Math.round(value * 100) !== value * 100;
  if (hasSubCentPrecision) return (lang === 'ru' ? RU_RATE : EN_RATE).format(value);
  return (lang === 'ru' ? RU_MONEY : EN_MONEY).format(value);
}
/* Read in UTC, because the value is a calendar date, not an instant.
 *
 * `new Date('2026-12-01')` is midnight UTC. Formatted in the reader's own
 * zone, that is 30 November for anyone west of Greenwich, so a deadline
 * bound as the first of the month renders as the month before — to a reader
 * in New York, not to the author who wrote it. Twenty-eight facts across
 * Estonia, Greece and Thailand use day 01 as exactly that kind of
 * month-precision placeholder. Pinning the formatter to UTC makes the day
 * component irrelevant, which is what a month-precision date wants. */
const RU_MONTH = new Intl.DateTimeFormat('ru-RU', { month: 'long', year: 'numeric', timeZone: 'UTC' });
const EN_MONTH = new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });

/* A date the reader is expected to act on keeps its day.
 *
 * `date` renders month and year, which is right for a month-precision
 * placeholder and wrong for a filing deadline: a Thai return is due on 31
 * March, and "March 2026" loses the only part that matters. Russian also
 * needs the day for grammar — without it Intl gives the nominative "март
 * 2026 г.", so every Russian preposition around the token reads wrong ("в
 * март 2026"), and a writer has to bend the sentence around the formatter.
 * With a day, Russian takes the genitive by itself: "31 марта 2026 г.".
 * English uses en-GB here so the day leads, matching how the corpus writes
 * dates in prose. Both are read in UTC, for the reason above. */
const RU_DATE_FULL = new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' });
const EN_DATE_FULL = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' });

function num(value: number, lang: LangCode): string {
  return (lang === 'ru' ? RU_NUM : EN_NUM).format(value);
}

/**
 * Picks the grammatically correct Russian plural form for a count.
 * Russian nouns after a number take one of three forms — e.g. for "year":
 * 1 год (one), 2-4 года (few), 5-20 лет (many) — and the pattern repeats
 * for every ten (21 год, 22-24 года, 25-30 лет, ...), except the teens
 * (11-14), which always take the "many" form regardless of the last digit.
 *
 * `forms` is [one, few, many], e.g. ruPlural(3, ['год', 'года', 'лет']).
 */
function ruPlural(n: number, forms: readonly [one: string, few: string, many: string]): string {
  const abs = Math.abs(Math.round(n));
  const mod10 = abs % 10;
  const mod100 = abs % 100;
  if (mod100 >= 11 && mod100 <= 14) return forms[2];
  if (mod10 === 1) return forms[0];
  if (mod10 >= 2 && mod10 <= 4) return forms[1];
  return forms[2];
}

/** Picks singular vs. plural for English: enPlural(1, 'year', 'years') -> 'year'. */
function enPlural(n: number, singular: string, plural: string): string {
  return Math.abs(Math.round(n)) === 1 ? singular : plural;
}

/** Exported for unit testing; also used internally by the registry below. */
export function formatValue(
  raw: unknown,
  format: FactFormat | undefined,
  lang: LangCode,
): string {
  if (raw == null) return '—';
  const ruSpace = ' '; // nbsp keeps "€1 700" together at line breaks

  switch (format) {
    case 'eur':
      if (typeof raw !== 'number') return String(raw);
      return `€${ruSpace}${money(raw, lang)}`;
    case 'eur_mo':
      if (typeof raw !== 'number') return String(raw);
      return lang === 'ru'
        ? `€${ruSpace}${money(raw, lang)}/мес`
        : `€${ruSpace}${money(raw, lang)}/mo`;
    case 'usd':
      if (typeof raw !== 'number') return String(raw);
      return `$${ruSpace}${money(raw, lang)}`;
    /* Baht amounts used to go through `raw`, which is String(raw): an
     * international school's 1197000 printed as "1197000", in prose and in the
     * bar beside it. The UAE's dirham figures ship the same way today. */
    case 'thb':
      if (typeof raw !== 'number') return String(raw);
      return lang === 'ru'
        ? `${money(raw, lang)}${ruSpace}бат`
        : `THB${ruSpace}${money(raw, lang)}`;
    case 'num':
      if (typeof raw !== 'number') return String(raw);
      return PCT_NUM(lang).format(raw);
    case 'pct': {
      if (typeof raw !== 'number') return String(raw);
      /* A pct value is a percentage. It is not sometimes a fraction.
       *
       * This used to read `Math.abs(raw) <= 1 ? raw * 100 : raw`, described as
       * accepting "both fractional (0.28) and percent-already (28) inputs".
       * The two conventions cannot be told apart, and every fact in the corpus
       * that landed in the ambiguous range meant the literal percentage. So the
       * heuristic had no correct users and eleven incorrect ones, each rendered
       * a hundred times too large on a live page:
       *
       *   Portugal  stamp_duty 0.8   ->  "80 %"
       *   Portugal  aimi_min   0.7   ->  "70 %"
       *   Spain     ajd_min    0.5   ->  "50 %"
       *   Estonia   land_tax_residential_max 1 -> "100 %"
       *   UAE       mortgage_reg 0.25 -> "25 %"
       *
       * The UAE property plan's own section intent reads "mortgage reg 0.25 %"
       * while the page printed 25 %. A reader budgeting a Portuguese purchase
       * was told stamp duty is eighty percent of the price.
       *
       * Formatting goes through Intl so Russian gets its decimal comma; the
       * old path built the string by hand and printed "21.9 %" on RU pages.
       * Precision is the author's to choose, as with money, so 2.05 stays
       * 2.05 rather than rounding to 2.1 — two shares that round to 2.1 and
       * 4.1 sum to 6.2 and contradict the 6.1 total stated beside them. */
      return `${PCT_NUM(lang).format(raw)}${ruSpace}%`;
    }
    case 'days':
      if (typeof raw !== 'number') return String(raw);
      return lang === 'ru'
        ? `${num(raw, lang)} ${ruPlural(raw, ['день', 'дня', 'дней'])}`
        : `${num(raw, lang)} ${enPlural(raw, 'day', 'days')}`;
    case 'months':
      if (typeof raw !== 'number') return String(raw);
      return lang === 'ru'
        ? `${num(raw, lang)} ${ruPlural(raw, ['месяц', 'месяца', 'месяцев'])}`
        : `${num(raw, lang)} ${enPlural(raw, 'month', 'months')}`;
    case 'years':
      if (typeof raw !== 'number') return String(raw);
      return lang === 'ru'
        ? `${num(raw, lang)} ${ruPlural(raw, ['год', 'года', 'лет'])}`
        : `${num(raw, lang)} ${enPlural(raw, 'year', 'years')}`;
    case 'date': {
      const d = typeof raw === 'string' ? new Date(raw) : null;
      if (!d || Number.isNaN(d.getTime())) return String(raw);
      return (lang === 'ru' ? RU_MONTH : EN_MONTH).format(d);
    }
    case 'date_full': {
      const d = typeof raw === 'string' ? new Date(raw) : null;
      if (!d || Number.isNaN(d.getTime())) return String(raw);
      return (lang === 'ru' ? RU_DATE_FULL : EN_DATE_FULL).format(d);
    }
    case 'raw':
    default:
      return String(raw);
  }
}

/* ────────────────────────────────────────────────────────────────── */
/*  Path resolver                                                      */
/* ────────────────────────────────────────────────────────────────── */
/*  Supports dot.path with optional [n] index segments.                */
/*  "regimes[0].pit_rate_pct" → ['regimes', 0, 'pit_rate_pct']         */

function resolvePath(root: unknown, path: string): unknown {
  if (root == null) return undefined;
  const segments = path
    .split('.')
    .flatMap((s) => {
      const m = s.match(/^([^[]+)((?:\[\d+\])*)$/);
      if (!m) return [s];
      const out: (string | number)[] = [m[1]];
      const idxRe = /\[(\d+)\]/g;
      let im: RegExpExecArray | null;
      while ((im = idxRe.exec(m[2])) !== null) out.push(Number(im[1]));
      return out;
    });

  let cursor: unknown = root;
  for (const seg of segments) {
    if (cursor == null) return undefined;
    cursor = (cursor as Record<string | number, unknown>)[seg];
  }
  return cursor;
}

/* ────────────────────────────────────────────────────────────────── */
/*  Source accessor                                                    */
/* ────────────────────────────────────────────────────────────────── */
/*  Each entry returns the per-country sub-object for the given cc, or */
/*  undefined if the source isn't loaded yet / cc is missing.          */

type SourceAccessor = (cc: CountryCode) => unknown;

function buildAccessors(opts: {
  countriesData: { countries: Country[] } | undefined;
  taxData: ReturnType<typeof useCountryTaxRegimes>['data'];
  visaData: ReturnType<typeof useCountryVisaExtra>['data'];
  propertyData: ReturnType<typeof usePropertyTax>['data'];
  carData: ReturnType<typeof useCarOwnership>['data'];
  passportData: ReturnType<typeof usePassportTreatment>['data'];
  nextStepsData: Record<string, unknown> | undefined;
  costData: ReturnType<typeof useCostOfLiving>['data'];
}): Partial<Record<FactSource, SourceAccessor>> {
  const {
    countriesData, taxData, visaData, propertyData, carData, passportData, nextStepsData,
    costData,
  } = opts;

  return {
    countries: (cc) =>
      countriesData?.countries.find((c) => c.code === cc),
    tax_regimes: (cc) =>
      (taxData as { countries?: Record<string, unknown> } | undefined)?.countries?.[cc],
    visa_extra: (cc) =>
      (visaData as { countries?: Record<string, unknown> } | undefined)?.countries?.[cc],
    property_tax: (cc) =>
      (propertyData as { countries?: Record<string, unknown> } | undefined)?.countries?.[cc],
    car_ownership: (cc) =>
      (carData as { countries?: Record<string, unknown> } | undefined)?.countries?.[cc],
    passport_treatment: (cc) =>
      (passportData as { countries?: Record<string, unknown> } | undefined)?.countries?.[cc],
    next_steps: (cc) => nextStepsData?.[cc],
    cost_of_living: (cc) => costData?.countries?.[cc],
  };
}

/* ────────────────────────────────────────────────────────────────── */
/*  Public registry                                                    */
/* ────────────────────────────────────────────────────────────────── */

export interface ResolvedFact {
  id: string;
  formatted: string;
  raw: unknown;
  lastVerified: string;
  note?: string;
}

export interface FactsRegistry {
  ready: boolean;
  /** Resolve one binding by id; returns null if unknown. */
  resolve: (id: string) => ResolvedFact | null;
  /** Replace every `{{fact.<id>}}` token in `text` with the formatted value. */
  interpolate: (text: string) => string;
  /** Date string YYYY-MM-DD = min(lastVerified) across all bindings. */
  freshness: string | null;
}

const EMPTY_REGISTRY: FactsRegistry = {
  ready: false,
  resolve: () => null,
  interpolate: (t) => t,
  freshness: null,
};

/**
 * Builds a registry for a specific article: pre-resolves every binding,
 * memoised on the binding list + cc + lang so callers can safely use it
 * in render without recomputing on every key-stroke.
 */
export function useFactsForArticle(
  bindings: FactBinding[],
  cc: CountryCode | null,
  lang: LangCode,
): FactsRegistry {
  const { data: countriesData } = useQuery(countriesQuery());
  const { data: taxData } = useCountryTaxRegimes();
  const { data: visaData } = useCountryVisaExtra();
  const { data: propertyData } = usePropertyTax();
  const { data: carData } = useCarOwnership();
  const { data: passportData } = usePassportTreatment();
  const { data: costData } = useCostOfLiving();
  const { data: nextStepsData } = useQuery<Record<string, unknown>>({
    queryKey: ['data', 'next-steps-by-country.json'],
    queryFn: async () => {
      const res = await fetch('/data/next-steps-by-country.json');
      if (!res.ok) throw new Error('next-steps fetch failed');
      return res.json();
    },
    staleTime: 7 * 24 * 60 * 60 * 1000,
  });

  return useMemo<FactsRegistry>(() => {
    if (!cc) return EMPTY_REGISTRY;

    // cost_of_living is gated on demand rather than unconditionally. An
    // unresolved binding formats as an em-dash, which reads as a typo in the
    // middle of a sentence, so an article that binds it should wait. An
    // article that does not bind it has no reason to wait at all.
    const needsCost = bindings.some((b) => b.source === 'cost_of_living');
    const allLoaded =
      !!countriesData && !!taxData && !!visaData &&
      !!propertyData && !!carData && !!passportData &&
      (!needsCost || !!costData);
    if (!allLoaded) return EMPTY_REGISTRY;

    const accessors = buildAccessors({
      countriesData, taxData, visaData, propertyData, carData, passportData,
      nextStepsData, costData,
    });

    const resolved = new Map<string, ResolvedFact>();
    for (const b of bindings) {
      let raw: unknown;
      if (b.source === 'manual') {
        raw = b.value ?? null;
      } else {
        const accessor = accessors[b.source];
        if (accessor && b.path) raw = resolvePath(accessor(cc), b.path);
      }
      if (raw == null && b.fallback != null) raw = b.fallback;

      resolved.set(b.id, {
        id: b.id,
        raw,
        formatted: formatValue(raw, b.format, lang),
        lastVerified: b.lastVerified,
        note: b.note,
      });
    }

    const interpolate = (text: string): string => {
      return text.replace(/\{\{\s*fact\.([a-zA-Z0-9_-]+)\s*\}\}/g, (_, id) => {
        const f = resolved.get(id);
        return f ? f.formatted : `{{fact.${id}}}`;
      });
    };

    const freshness = bindings.length === 0
      ? null
      : bindings
          .map((b) => b.lastVerified)
          .sort()
          .at(0) ?? null;

    return {
      ready: true,
      resolve: (id) => resolved.get(id) ?? null,
      interpolate,
      freshness,
    };
  }, [
    bindings, cc, lang,
    countriesData, taxData, visaData, propertyData,
    carData, passportData, nextStepsData, costData,
  ]);
}
